import { useState, useEffect, useRef } from "react";
import { ImageIcon, RefreshCw, AlertCircle } from "lucide-react";

/**
 * OptimizedImage - A smart image component with:
 * - Lazy loading with Intersection Observer
 * - Loading skeleton placeholder
 * - Error handling with retry
 * - Graceful fallback
 * - Progressive loading effect
 */
const OptimizedImage = ({ 
  src, 
  alt = "", 
  className = "", 
  fallbackSrc = null,
  maxRetries = 2,
  retryDelay = 1000,
  showPlaceholder = true,
  aspectRatio = "aspect-square",
  onLoad = null,
  onError = null,
  ...props 
}) => {
  const [status, setStatus] = useState("loading"); // loading | loaded | error
  const [retryCount, setRetryCount] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  // Reset state when src changes
  useEffect(() => {
    setStatus("loading");
    setRetryCount(0);
    setCurrentSrc(src);
  }, [src]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: "100px", // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleLoad = () => {
    setStatus("loaded");
    onLoad?.();
  };

  const handleError = () => {
    if (retryCount < maxRetries) {
      // Retry with exponential backoff
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        // Add cache-busting query param
        const bustCache = currentSrc.includes("?") 
          ? `${currentSrc}&_retry=${Date.now()}` 
          : `${currentSrc}?_retry=${Date.now()}`;
        setCurrentSrc(bustCache);
      }, retryDelay * (retryCount + 1));
    } else if (fallbackSrc && currentSrc !== fallbackSrc) {
      // Try fallback src
      setCurrentSrc(fallbackSrc);
      setRetryCount(0);
    } else {
      setStatus("error");
      onError?.();
    }
  };

  const handleRetryClick = () => {
    setStatus("loading");
    setRetryCount(0);
    setCurrentSrc(`${src}?_retry=${Date.now()}`);
  };

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden bg-muted/30 ${aspectRatio} ${className}`}
      {...props}
    >
      {/* Loading Skeleton */}
      {(status === "loading" || !isInView) && showPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center gap-2">
            <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
            {retryCount > 0 && (
              <span className="text-xs text-muted-foreground">
                Retry {retryCount}/{maxRetries}...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2">
          <AlertCircle className="w-6 h-6 text-muted-foreground/70" />
          <span className="text-xs text-muted-foreground text-center">Failed to load</span>
          <button
            onClick={handleRetryClick}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        </div>
      )}

      {/* Actual Image */}
      {isInView && status !== "error" && (
        <img
          src={currentSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`
            w-full h-full object-cover transition-opacity duration-300
            ${status === "loaded" ? "opacity-100" : "opacity-0"}
          `}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
};

export default OptimizedImage;
